import { useEffect } from 'react';

import { getMemberId } from '@trello/authentication';
import { deepEqual } from '@trello/objects';
import { PersistentSharedState, useSharedState } from '@trello/shared-state';

import {
  useMemberCohortsFragment,
  type MemberCohortsFragment,
} from './MemberCohortsFragment.generated';

export interface MemberCohortsSharedState {
  cohorts: Record<string, string> | undefined;
}

const cohortsSharedState: MemberCohortsSharedState = {
  cohorts: undefined,
};

const makeCohorts = (
  memberData: MemberCohortsFragment | undefined,
): MemberCohortsSharedState => {
  const personalProductivityCohort =
    memberData?.cohorts?.userCohortPersonalProductivity;
  const referralPilotCohort = memberData?.cohorts?.userCohortReferralPilot;
  if (!personalProductivityCohort && !referralPilotCohort) {
    return {
      cohorts: undefined,
    };
  }

  return {
    cohorts: {
      ...(personalProductivityCohort && {
        userCohortPersonalProductivity: personalProductivityCohort,
      }),
      ...(referralPilotCohort && {
        userCohortReferralPilot: referralPilotCohort,
      }),
    },
  };
};

// We version the key so that stale web versions do not try to write values
// with an old data shape to local storage.
// Please update this value when MemberCohortsSharedState type definition changes.
const version = 'ab8fc5bd18013af887857567110ee2e5f4633454';

const memberCohortsSharedState =
  new PersistentSharedState<MemberCohortsSharedState>(cohortsSharedState, {
    storageKey: () =>
      `memberCohorts-${getMemberId() || 'anonymous'}-${version}`,
  });

export const useCachedMemberCohorts: (
  trelloMemberId: string,
) => MemberCohortsSharedState = (trelloMemberId) => {
  const { data: cohortsData, complete: isCohortDataLoaded } =
    useMemberCohortsFragment({
      from: { id: trelloMemberId },
    });

  const [cohortsState, setMemberCohorts] = useSharedState(
    memberCohortsSharedState,
  );

  useEffect(() => {
    if (!isCohortDataLoaded) {
      return;
    }

    const fetchedCohorts = makeCohorts(cohortsData);
    if (!deepEqual(cohortsState, fetchedCohorts)) {
      setMemberCohorts(fetchedCohorts);
    }
  }, [isCohortDataLoaded, cohortsData, setMemberCohorts, cohortsState]);

  if (!isCohortDataLoaded) {
    return cohortsState;
  }

  return makeCohorts(cohortsData);
};
